/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable max-len */
import React from "react"
import { useSelector } from "react-redux"

import {
    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
    IEC_61508_1998,
    IEC_61508_2010,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
    NO_ARCHITECTURAL_CONSTRAINTS,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO,
    ARCHITECTURE_1oo1,
} from "const"
import { SilResultProjectDevice, SilResultSafetyFunction } from "server/models"
import {
    SilTarget,
    SilCalculationType,
    YesNo,
} from "client/utils"
import { LangType, StoreType, UserSettings } from "client/store"

import { FormatProbabilityString } from "client/pages/sil/format/probability"
import * as Formulas from "client/pages/sil/formulas"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
    BlockContentRow,
    BlockContentTitle,
} from "../styled"

const PFD = "pfd"
const PFH = "pfh"

interface CalculationProps {
    resultSafetyFunction: SilResultSafetyFunction
}

const Calculation = ({
    resultSafetyFunction,
}: CalculationProps) => {
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const result = Calculate(resultSafetyFunction, userSettings, lang)

    return (
        <div>
            <ResultTable>
                <thead>
                    <tr>
                        {
                            result.columns.map((column) => (
                                <th key={column}>{column}</th>
                            ))
                        }
                    </tr>
                </thead>

                <tbody>
                    {
                        result.rows.map((row, indexRow) => (
                            <tr key={indexRow}>
                                {
                                    row.map((column, indexColumn) => (
                                        (indexRow === result.rows.length - 1
                                        && indexColumn === 0)
                                            ? <td key={indexColumn}><b>{column}</b></td>
                                            : <td key={indexColumn}>{column}</td>
                                    ))
                                }
                            </tr>
                        ))
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>{result.annotation}</ResultTableAnnotation>

            <BlockContent>
                {
                    result.output.map((row) => (
                        <BlockContentRow key={row.title}>
                            <BlockContentTitle>{row.title}</BlockContentTitle>
                            {row.value}
                        </BlockContentRow>
                    ))
                }

                {
                    /* PFDavg/PFH Target X is met/not met */
                    result.targetMetPfd && (
                        <BlockContentRow>
                            <BlockContentTitle>{result.targetMetPfd}</BlockContentTitle>
                        </BlockContentRow>
                    )
                }
                {
                    // target SIL X is achieved/not achieved for this hardware
                    result.targetSilAchieved && (
                        <BlockContentRow>
                            <BlockContentTitle>{result.targetSilAchieved}</BlockContentTitle>
                        </BlockContentRow>
                    )
                }

                {
                    /* STR Target X is met/not met */
                    result.targetMetStr && (
                        <BlockContentRow>
                            <BlockContentTitle>{result.targetMetStr}</BlockContentTitle>
                        </BlockContentRow>
                    )
                }
                {
                    // target STL X is achieved/not achieved for this hardware
                    result.targetStlAchieved && (
                        <BlockContentRow>
                            <BlockContentTitle>{result.targetStlAchieved}</BlockContentTitle>
                        </BlockContentRow>
                    )
                }
            </BlockContent>
        </div>
    )
}
export default Calculation


export const Calculate = (
    resultSafetyFunction: SilResultSafetyFunction,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const { percentageDecimal } = userSettings.sil
    const {
        calculationMethodDangerous,
        calculationMethodSafe,
        mode,
        targetSil,
        targetStl,
        subsystems,
    } = resultSafetyFunction

    const targetPfd = resultSafetyFunction.targetPfd !== null
        ? Formulas.parseProp(resultSafetyFunction.targetPfd)
        : Formulas.getMaxProbability(mode, targetSil)

    const targetStr = resultSafetyFunction.targetStr !== null
        ? Formulas.parseProp(resultSafetyFunction.targetStr)
        : Formulas.getMaxStr(targetStl).maxStr

    // don't show "SIL Architecture" column if subsystems have "No Architecture Constraints"
    let showSilArchitecture = true
    subsystems.forEach((subsystem) => {
        if (subsystem.architecturalConstraints === NO_ARCHITECTURAL_CONSTRAINTS) {
            showSilArchitecture = false
        }
    })

    // define calculation type
    let calcType: SilCalculationType = null
    if (
        [
            IEC_61508_2010,
            IEC_61508_1998,
            // ISA_TR84_2002, << TODO
            ISA_TR84_2022,
        ].includes(calculationMethodDangerous)
    ) {
        if (mode === LOW_DEMAND_MODE) calcType = PFD
        if (mode === HIGH_DEMAND_MODE || mode === CONTINUOUS_MODE) calcType = PFH
    }
    if (calculationMethodDangerous === VDI_VDE_2180_2019) calcType = PFD
    // for calculationMethodSafe we don't define calcType

    // initialize variables
    let finalPfd: number = 0
    let finalPfh: number = 0
    let finalStr: number = 0

    let finalSil: SilTarget | 0 = null
    let finalStl: number = null

    let finalPfdSilTarget: string
    let finalPfhSilTarget: string
    let finalStrStlTarget: string

    let isMetPfd: boolean
    let targetMetPfd: string

    let isMetStr: boolean
    let targetMetStr: string

    let targetSilAchieved: string = ""
    let targetStlAchieved: string = ""


    const columns: string[] = []
    columns.push(lang.subsystems)
    if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
        if (calcType === PFD) columns.push(lang.pfd)
        if (calcType === PFH) columns.push(lang.pfh)
        if (targetSil) columns.push(`% ${lang[`sil_${targetSil}`]} ${lang.target}`)
        if (showSilArchitecture) columns.push(lang.silArchitecture)
    }
    if (calculationMethodSafe !== NO_SAFE_FAILURE) {
        columns.push(lang.str)
        if (targetStl) columns.push(lang.targetStlPercents.replace("{0}", targetStl.toString()))
        columns.push(lang.stlLevels)
    }

    const rows: string[][] = []
    subsystems.forEach((subsystem) => {
        const { architecture } = subsystem

        let ptc0A: number = 0
        let ptc1A: number = 0
        let ptc0B: number = 0
        let ptc1B: number = 0
        let ptc0C: number = 0
        let ptc1C: number = 0

        let mttr: number = 0
        let mrt: number = 0
        let beta: number = 0
        let betaD: number = 0

        let ti: number = 0
        let di: number = 0

        let t0: number = 0
        let t1: number = 0
        let t2: number = 0

        let k: number = 0
        let safeFailure: number = 0
        let dangerousFailure: number = 0

        let safeState: YesNo = NO

        if (calculationMethodDangerous === IEC_61508_2010) {
            mttr = Formulas.parseProp(subsystem.iec615082010mttr)
            mrt = Formulas.parseProp(subsystem.iec615082010mrt)
            beta = Formulas.parseProp(subsystem.iec615082010beta)
            betaD = Formulas.parseProp(subsystem.iec615082010betaD)
            t1 = Formulas.parseProp(subsystem.iec615082010t1)
            k = Formulas.parseProp(subsystem.iec615082010k)
        }
        if (calculationMethodDangerous === IEC_61508_1998) {
            mttr = Formulas.parseProp(subsystem.iec615081998mttr)
            beta = Formulas.parseProp(subsystem.iec615081998beta)
            t1 = Formulas.parseProp(subsystem.iec615081998t1)
        }
        if (calculationMethodDangerous === ISA_TR84_2002 || calculationMethodSafe === ISA_TR84_2002) { // TODO
            mttr = Formulas.parseProp(subsystem.tr842002mttr)
            beta = Formulas.parseProp(subsystem.tr842002beta)
            t1 = Formulas.parseProp(subsystem.tr842002t1)
            safeFailure = Formulas.parseProp(subsystem.tr842002safeFailure)
            dangerousFailure = Formulas.parseProp(subsystem.tr842002dangerousFailure)
        }
        if (calculationMethodDangerous === ISA_TR84_2022 || calculationMethodSafe === ISA_TR84_2022) {
            ti = Formulas.parseProp(subsystem.isatr842022ti)
            di = Formulas.parseProp(subsystem.isatr842022di)
            mttr = Formulas.parseProp(subsystem.isatr842022mttr)
            beta = Formulas.parseProp(subsystem.isatr842022beta)
            safeState = subsystem.isatr842022safeState
        }
        if (calculationMethodDangerous === VDI_VDE_2180_2019) {
            ptc0A = Formulas.parseProp(subsystem.vdivde21802019ptc0A)
            ptc1A = Formulas.parseProp(subsystem.vdivde21802019ptc1A)
            ptc0B = Formulas.parseProp(subsystem.vdivde21802019ptc0B)
            ptc1B = Formulas.parseProp(subsystem.vdivde21802019ptc1B)
            ptc0C = Formulas.parseProp(subsystem.vdivde21802019ptc0C)
            ptc1C = Formulas.parseProp(subsystem.vdivde21802019ptc1C)
            beta = Formulas.parseProp(subsystem.vdivde21802019beta)
            t0 = Formulas.parseProp(subsystem.vdivde21802019t0)
            t1 = Formulas.parseProp(subsystem.vdivde21802019t1)
            t2 = Formulas.parseProp(subsystem.vdivde21802019t2)
        }

        const calculatedPfd: number[] = []
        const calculatedPfh: number[] = []
        const calculatedStr: number[] = []

        const failureRateYesDevices: SilResultProjectDevice[] = []
        const failureRateNoDevices: SilResultProjectDevice[] = []

        subsystem.blocks.forEach((block) => {
            const { projectDevice } = block
            if (projectDevice.failureRate) failureRateYesDevices.push(projectDevice)
            else failureRateNoDevices.push(projectDevice)
        })

        // calculation method safe
        if (
            [
                ISA_TR84_2002,
                ISA_TR84_2022,
            ].includes(calculationMethodSafe)
        ) {
            subsystem.blocks.forEach((block) => {
                const { projectDevice } = block

                const { sd, su, dd, du } = Formulas.parseProps({
                    sd: projectDevice.sd,
                    su: projectDevice.su,
                    dd: projectDevice.dd,
                    du: projectDevice.du,
                })

                if (calculationMethodSafe === ISA_TR84_2002) { // TODO
                    const calculatedData = Formulas.isa_tr84_2002_PFD({ architecture, mttr, beta, t1, safeFailure, dangerousFailure, sd, su, dd, du })
                    calculatedStr.push(calculatedData.str)
                }
                if (calculationMethodSafe === ISA_TR84_2022) {
                    const str = Formulas.isa_tr84_2022_STR({ architecture, ti, mttr, safeState, sd, su, dd })
                    calculatedStr.push(str)
                }
            })
        }

        // calculation method dangerous
        if (
            [
                IEC_61508_2010,
                IEC_61508_1998,
                ISA_TR84_2002,
                ISA_TR84_2022,
            ].includes(calculationMethodDangerous)
        ) {
            if (failureRateYesDevices.length === subsystem.blocks.length) {
                // all devices have "failureRate" = "true"

                subsystem.blocks.forEach((block) => {
                    const { projectDevice } = block

                    const { sd, /* su, */ dd, du } = Formulas.parseProps({
                        sd: projectDevice.sd,
                        su: projectDevice.su,
                        dd: projectDevice.dd,
                        du: projectDevice.du,
                    })

                    let calculatedData: number
                    if (calculationMethodDangerous === IEC_61508_2010) {
                        calculatedData = Formulas.iec_61508_2010_PFD({ calcType, architecture, mttr, mrt, beta, betaD, t1, k, sd, dd, du })
                    }
                    if (calculationMethodDangerous === IEC_61508_1998) {
                        calculatedData = Formulas.iec_61508_1998_PFD({ calcType, architecture, mttr, beta, t1, sd, dd, du })
                    }
                    /*
                    if (calculationMethodDangerous === ISA_TR84_2002) { // TODO
                        const calculatedData = Formulas.isa_tr84_2002_PFD({ architecture, mttr, beta, t1, safeFailure, dangerousFailure, sd, su, dd, du })
                        calculatedPfd.push(calculatedData.pfd)
                        // TODO: for pfh
                    }
                    */
                    if (calculationMethodDangerous === ISA_TR84_2022) {
                        const calculatedDC = Formulas.dc({ // use calculated DC when failureRate is true
                            dd: projectDevice.dd,
                            du: projectDevice.du,
                        })
                        calculatedData = Formulas.isa_tr84_2022_PFD({ calcType, architecture, ti, di, mttr, beta, safeState, sd, dd, du, dc: Number(calculatedDC) })
                    }

                    if (calcType === PFD) calculatedPfd.push(calculatedData)
                    if (calcType === PFH) calculatedPfh.push(calculatedData)
                })
            } else if (failureRateNoDevices.length === subsystem.blocks.length) {
                // all devices have "failureRate" = "false"

                subsystem.blocks.forEach((block) => {
                    const { projectDevice } = block

                    const { pfd, pfh } = Formulas.parseProps({
                        pfd: projectDevice.pfd,
                        pfh: projectDevice.pfh,
                    })

                    // use the same function both for PFD and PFH
                    if (calcType === PFD) {
                        const calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd, beta })
                        calculatedPfd.push(calculatedData)
                    }
                    if (calcType === PFH) {
                        const calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd: pfh, beta })
                        calculatedPfh.push(calculatedData)
                    }

                    /* // TODO
                    if (calculationMethodDangerous === ISA_TR84_2002) {
                        calculatedPfd.push(pfd)
                        // TODO: for pfh
                    }
                    */
                })
            } else {
                // both devices with "failureRate" = "true" and "failureRate" = "false"

                subsystem.blocks.forEach((block) => {
                    const { projectDevice } = block
                    const { failureRate } = projectDevice

                    const { sd, /* su, */ dd, du, pfd, pfh } = Formulas.parseProps({
                        sd: projectDevice.sd,
                        su: projectDevice.su,
                        dd: projectDevice.dd,
                        du: projectDevice.du,
                        pfd: projectDevice.pfd,
                        pfh: projectDevice.pfh,
                    })

                    if (failureRate) {
                        let calculatedData: number
                        if (calculationMethodDangerous === IEC_61508_2010) {
                            calculatedData = Formulas.iec_61508_2010_PFD({ calcType, architecture: ARCHITECTURE_1oo1, mttr, mrt, beta, betaD, t1, k, sd, dd, du })
                        }
                        if (calculationMethodDangerous === IEC_61508_1998) {
                            calculatedData = Formulas.iec_61508_1998_PFD({ calcType, architecture: ARCHITECTURE_1oo1, mttr, beta, t1, sd, dd, du })
                        }
                        /*
                        if (calculationMethodDangerous === ISA_TR84_2002) { // TODO
                            const calculatedData = Formulas.isa_tr84_2002_PFD({ architecture: ARCHITECTURE_1oo1, mttr, beta, t1, safeFailure, dangerousFailure, sd, su, dd, du })
                            calculatedPfd.push(calculatedData.pfd)
                            // TODO: for pfh
                        }
                        */
                        if (calculationMethodDangerous === ISA_TR84_2022) {
                            const calculatedDC = Formulas.dc({ // use calculated DC when failureRate is true
                                dd: projectDevice.dd,
                                du: projectDevice.du,
                            })
                            calculatedData = Formulas.isa_tr84_2022_PFD({ calcType, architecture: ARCHITECTURE_1oo1, ti, di, mttr, beta, safeState, sd, dd, du, dc: Number(calculatedDC) })
                        }

                        // use the same function both for PFD and PFH
                        calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd: calculatedData, beta })

                        if (calcType === PFD) calculatedPfd.push(calculatedData)
                        if (calcType === PFH) calculatedPfh.push(calculatedData)
                    }
                    if (!failureRate) {
                        // use the same function both for PFD and PFH
                        if (calcType === PFD) {
                            const calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd, beta })
                            calculatedPfd.push(calculatedData)
                        }
                        if (calcType === PFH) {
                            const calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd: pfh, beta })
                            calculatedPfh.push(calculatedData)
                        }

                        /* // TODO
                        if (calculationMethodDangerous === ISA_TR84_2002) {
                            calculatedPfd.push(pfd)
                            // TODO: for pfh
                        }
                        */
                    }
                })
            }
        }

        if (calculationMethodDangerous === VDI_VDE_2180_2019) {
            const blockA = subsystem.blocks[0]
            const blockB = subsystem.blocks[1]
            const blockC = subsystem.blocks[2]

            if (failureRateYesDevices.length === subsystem.blocks.length) {
                // all devices have "failureRate" = "true"
                const duA = blockA && blockA.projectDevice ? Formulas.parseProp(blockA.projectDevice.du) : null
                const duB = blockB && blockB.projectDevice ? Formulas.parseProp(blockB.projectDevice.du) : null
                const duC = blockC && blockC.projectDevice ? Formulas.parseProp(blockC.projectDevice.du) : null

                const calculated = Formulas.vdi_vde_2180_2019_PFD({ architecture, ptc0A, ptc1A, ptc0B, ptc1B, ptc0C, ptc1C, beta, t0, t1, t2, duA, duB, duC })
                calculatedPfd.push(calculated)
            } else if (failureRateNoDevices.length === subsystem.blocks.length) {
                // all devices have "failureRate" = "false"
                const pfdA = blockA && blockA.projectDevice ? Formulas.parseProp(blockA.projectDevice.pfd) : null
                const pfdB = blockB && blockB.projectDevice ? Formulas.parseProp(blockB.projectDevice.pfd) : null
                const pfdC = blockC && blockC.projectDevice ? Formulas.parseProp(blockC.projectDevice.pfd) : null

                const calculatedPfdA = Formulas.pfdFailureRateFalse({ architecture, pfd: pfdA, beta })
                const calculatedPfdB = Formulas.pfdFailureRateFalse({ architecture, pfd: pfdB, beta })
                const calculatedPfdC = Formulas.pfdFailureRateFalse({ architecture, pfd: pfdC, beta })

                calculatedPfd.push(calculatedPfdA, calculatedPfdB, calculatedPfdC)
            } else {
                // both devices with "failureRate" = "true" and "failureRate" = "false"
                subsystem.blocks.forEach((block) => {
                    const { projectDevice } = block
                    const { failureRate } = projectDevice

                    const { du, pfd } = Formulas.parseProps({
                        du: projectDevice.du,
                        pfd: projectDevice.pfd,
                    })

                    if (failureRate) {
                        let calculatedData = Formulas.vdi_vde_2180_2019_PFD({
                            architecture: ARCHITECTURE_1oo1,
                            ptc0A,
                            ptc1A,
                            ptc0B,
                            ptc1B,
                            ptc0C,
                            ptc1C,
                            beta,
                            t0,
                            t1,
                            t2,
                            duA: du,
                            duB: null,
                            duC: null,
                        })

                        calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd: calculatedData, beta })
                        calculatedPfd.push(calculatedData)
                    }
                    if (!failureRate) {
                        const calculatedData = Formulas.pfdFailureRateFalse({ architecture, pfd, beta })
                        calculatedPfd.push(calculatedData)
                    }
                })
            }
        }

        // get the max value, if the max value is "false" or "null" Math.max returns "0"
        const subsystemPfd = calculatedPfd.length > 0 ? Math.max(...calculatedPfd) : null
        if (subsystemPfd === null || subsystemPfd === 0) finalPfd = 0
        else finalPfd += subsystemPfd

        const subsystemPfh = calculatedPfh.length > 0 ? Math.max(...calculatedPfh) : null
        if (subsystemPfh === null || subsystemPfh === 0) finalPfh = 0
        else finalPfh += subsystemPfh

        const subsystemStr = calculatedStr.length > 0 ? Math.max(...calculatedStr) : null
        if (subsystemStr === null || subsystemStr === 0) finalStr = 0
        else finalStr += subsystemStr

        // calculate final SIL architecture
        const silSubsystem = Formulas.architecturalConstraintsSubsystem(subsystem, targetSil, lang)
        const silSubsystemParsed = Formulas.parseSilValue(silSubsystem, lang) // "0" if "Not Compliant"
        if (finalSil === null) finalSil = silSubsystemParsed
        else if (finalSil > silSubsystemParsed) finalSil = silSubsystemParsed

        // % SIL/STL X Target
        finalPfdSilTarget = ((finalPfd / targetPfd) * 100).toFixed(percentageDecimal)
        finalPfhSilTarget = ((finalPfh / targetPfd) * 100).toFixed(percentageDecimal)
        finalStrStlTarget = ((finalStr / targetStr) * 100).toFixed(percentageDecimal)

        // calculate final STL
        const stlSubsystem = Formulas.determineStlLevel(subsystemStr)
        if (finalStl === null) finalStl = stlSubsystem
        else if (finalStl > stlSubsystem) finalStl = stlSubsystem

        if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
            // PFD/PFH Target X is met/not met
            if (calcType === PFD) {
                isMetPfd = finalPfd <= targetPfd
                targetMetPfd = isMetPfd ? lang.pfdTargetIsMet : lang.pfdTargetIsNotMet
            }
            if (calcType === PFH) {
                isMetPfd = finalPfh <= targetPfd
                targetMetPfd = isMetPfd ? lang.pfhTargetIsMet : lang.pfhTargetIsNotMet
            }

            // target SIL X is achieved/not achieved for this hardware
            if (showSilArchitecture) {
                targetSilAchieved = (finalSil >= targetSil && isMetPfd) ? lang.targetSilAchieved : lang.targetSilNotAchieved
            } else {
                targetSilAchieved = isMetPfd ? lang.targetSilAchieved : lang.targetSilNotAchieved
            }
        }
        if (calculationMethodSafe !== NO_SAFE_FAILURE) {
            if (targetStl) { // no targetStl - no targetStr
                // STR Target X is met/not met
                isMetStr = finalStr <= targetStr
                targetMetStr = isMetStr ? lang.strTargetIsMet : lang.strTargetIsNotMet

                // target STL X is achieved/not achieved for this hardware
                targetStlAchieved = (finalStl >= targetStl && isMetStr) ? lang.targetStlAchieved : lang.targetStlNotAchieved
            }
        }

        const row: string[] = []
        const subsystemPfdFormatted = FormatProbabilityString(subsystemPfd, userSettings, lang)
        const subsystemPfhFormatted = FormatProbabilityString(subsystemPfh, userSettings, lang)
        const subsystemStrFormatted = FormatProbabilityString(subsystemStr, userSettings, lang)

        row.push(subsystem.name)
        if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
            if (calcType === PFD) row.push(subsystemPfdFormatted)
            if (calcType === PFH) row.push(subsystemPfhFormatted)
            if (targetSil) {
                let subsystemTarget: number
                if (calcType === PFD) subsystemTarget = subsystemPfd
                if (calcType === PFH) subsystemTarget = subsystemPfh
                row.push(`${((subsystemTarget / targetPfd) * 100).toFixed(percentageDecimal)} %`)
            }
            if (showSilArchitecture) row.push(silSubsystem)
        }
        if (calculationMethodSafe !== NO_SAFE_FAILURE) {
            row.push(subsystemStrFormatted)
            if (targetStl) {
                row.push(`${((subsystemStr / targetStr) * 100).toFixed(percentageDecimal)} %`)
            }
            row.push(lang.stlLevel.replace("{0}", stlSubsystem.toString()))
        }

        rows.push(row)
    })


    const finalRow: string[] = []
    const finalPfdFormatted = finalPfd !== 0 ? FormatProbabilityString(finalPfd, userSettings, lang) : lang.n_a
    const finalPfhFormatted = finalPfh !== 0 ? FormatProbabilityString(finalPfh, userSettings, lang) : lang.n_a
    const finalStrFormatted = finalStr !== 0 ? FormatProbabilityString(finalStr, userSettings, lang) : lang.n_a

    finalRow.push(lang.safetyFunction)
    if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
        if (calcType === PFD) finalRow.push(finalPfdFormatted)
        if (calcType === PFH) finalRow.push(finalPfhFormatted)
        if (targetSil) {
            let finalTarget: string
            if (calcType === PFD) finalTarget = finalPfdSilTarget
            if (calcType === PFH) finalTarget = finalPfhSilTarget
            finalRow.push(finalTarget ? `${finalTarget} %` : lang.n_a)
        }
        if (showSilArchitecture) {
            finalRow.push(finalSil >= targetSil ? lang.compliant : lang.notCompliant)
        }
    }
    if (calculationMethodSafe !== NO_SAFE_FAILURE) {
        finalRow.push(finalStrFormatted)
        if (targetStl) {
            finalRow.push(finalStrStlTarget ? `${finalStrStlTarget} %` : lang.n_a)
            finalRow.push(finalStl >= targetStl ? lang.compliant : lang.notCompliant)
        } else {
            finalRow.push(lang.stlLevel.replace("{0}", finalStl.toString()))
        }
    }

    rows.push(finalRow)


    const output: {
        title: string
        value: string
    }[] = []

    if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
        if (calcType === PFD) {
            output.push({
                title: `${lang.silBasedOnPfd}: `,
                value: Formulas.determineSil(finalPfd, mode, lang),
            })
        }
        if (calcType === PFH) {
            output.push({
                title: `${lang.silBasedOnPfh}: `,
                value: Formulas.determineSil(finalPfh, mode, lang),
            })
        }

        // SIL based on architecture
        if (showSilArchitecture) {
            output.push({
                title: `${lang.silBasedOnArchitecture}: `,
                value: finalSil === 0 ? lang.notCompliant : lang[`sil_${finalSil}`],
            })
        }
    }
    if (calculationMethodSafe !== NO_SAFE_FAILURE) {
        output.push({
            title: `${lang.stlLevelBasedOnStr}: `,
            value: lang.stlLevel.replace("{0}", finalStl.toString()),
        })
    }

    return {
        columns,
        rows,
        annotation: `${lang.n_u} = ${lang.notUsed}, ${lang.n_a} = ${lang.notAllowed}`,
        output,

        targetMetPfd: calculationMethodDangerous !== NO_DANGEROUS_FAILURE
            ? targetMetPfd.replace("{0}", FormatProbabilityString(targetPfd, userSettings, lang))
            : null,
        targetSilAchieved: calculationMethodDangerous !== NO_DANGEROUS_FAILURE && targetSil
            ? targetSilAchieved.replace("{0}", lang[`sil_${targetSil}`])
            : null,

        targetMetStr: calculationMethodSafe !== NO_SAFE_FAILURE && targetStl // no targetStl - no targetStr
            ? targetMetStr.replace("{0}", FormatProbabilityString(targetStr, userSettings, lang))
            : null,
        targetStlAchieved: calculationMethodSafe !== NO_SAFE_FAILURE && targetStl
            ? targetStlAchieved.replace("{0}", targetStl.toString())
            : null,
    }
}
